// Single source of truth for which military aviation selection system a user
// is training for. Dashboard, Scores, the Full Battery, Guides, and
// recommendations all read module membership and identity text from here —
// do not duplicate a per-service module list anywhere else.
import { MODULES, MODULE_BY_ID } from '../constants/modules';
import { CFAST_TOTAL_MODULE_IDS } from './cbatScoreRequirements';

// Every service profile here is military (this file's own scope, per the
// header comment). Commercial-track modules (preparationFamily: 'commercial')
// are excluded from the "every module" fallback used by 'adf'/'general' so
// they never silently enroll into a military profile's module set.
const ALL_MODULE_IDS = MODULES
  .filter((module) => module.preparationFamily !== 'commercial')
  .map((module) => module.id);

// The 23-test RAF-oriented module set. FLAG replaces the older Colours,
// Letters & Numbers task as the RAF-side multitasking test; Mathematics
// Reasoning is not part of this set. This mapping reflects independently
// compiled and candidate-reported information, not an official RAF
// publication — see each module's guide entry for source status.
const RAF_MODULE_IDS = [
  'airborne-numerical',
  'angles-bearings-degrees',
  'auditory-capacity',
  'cognitive-updating',
  'figures-logistics-groups',
  'digit-recognition',
  'directions-distances',
  'dynamic-projection',
  'instrument-comprehension',
  'numerical-operations',
  'rapid-tracking',
  'sensory-motor',
  'situational-awareness',
  'spatial-integration',
  'system-logic',
  'table-reading',
  'target-recognition',
  'trace-test',
  'trace-test-2',
  'verbal-logic',
  'visual-search',
  'visualisation-tests',
  'vigilance'
];

const assertKnownModules = (moduleIds, profileId) => {
  moduleIds.forEach((moduleId) => {
    if (!MODULE_BY_ID[moduleId]) {
      throw new Error(`Unknown module id "${moduleId}" referenced by service profile "${profileId}".`);
    }
  });
  return moduleIds;
};

export const SERVICE_PROFILE_IDS = ['cfast', 'raf', 'adf', 'general'];

export const SERVICE_PROFILES = {
  cfast: {
    id: 'cfast',
    label: 'Canadian Armed Forces',
    shortLabel: 'CFAST',
    fullLabel: 'CFAST Training Profile',
    flag: 'ca',
    scoreOrganizationIds: ['cfast'],
    moduleIds: assertKnownModules(CFAST_TOTAL_MODULE_IDS, 'cfast'),
    mockTestProfileId: 'cfast-battery-v1',
    onboardingSummary: 'Trains the 20 documented CFAST tests.',
    disclaimer: 'Module groupings and scoring are independently developed by CBAT Academy from publicly available CFAST candidate material. They are not official Canadian Armed Forces scoring.'
  },
  raf: {
    id: 'raf',
    label: 'Royal Air Force',
    shortLabel: 'CBAT',
    fullLabel: 'CBAT Training Profile',
    flag: 'gb',
    scoreOrganizationIds: ['raf', 'navy'],
    moduleIds: assertKnownModules(RAF_MODULE_IDS, 'raf'),
    mockTestProfileId: 'raf-service-profile-v1',
    onboardingSummary: 'Trains the RAF-oriented module set, with FLAG in place of the older Colours, Letters & Numbers task.',
    disclaimer: 'This module set reflects candidate-reported and independently compiled information about the current CBAT battery. It is not published by the RAF and may not match the exact official test composition.'
  },
  adf: {
    id: 'adf',
    label: 'Australian Defence Force',
    shortLabel: 'Aviation Aptitude',
    fullLabel: 'Aviation Aptitude Training',
    flag: 'au',
    scoreOrganizationIds: ['adf'],
    moduleIds: assertKnownModules(ALL_MODULE_IDS, 'adf'),
    mockTestProfileId: 'academy-full-v1',
    onboardingSummary: 'Trains cognitive and psychomotor abilities relevant to Australian military aviation selection.',
    disclaimer: "CBAT Academy does not have sufficient public evidence to claim these exercises reproduce Australia's MACTS/OATB test battery. This is an Academy aptitude-training profile, not an official Australian battery, and no official Australian pass/fail thresholds are shown — the Scores page compares your training against a clearly-labeled Academy preparation target instead."
  },
  general: {
    id: 'general',
    label: 'General Aircrew Aptitude',
    onboardingLabel: 'General / Not sure',
    shortLabel: 'All Training Modules',
    fullLabel: 'All Training Modules',
    flag: null,
    scoreOrganizationIds: [],
    moduleIds: assertKnownModules(ALL_MODULE_IDS, 'general'),
    mockTestProfileId: 'academy-full-v1',
    onboardingSummary: 'Not preparing for a specific service yet, or want access to every training module.',
    disclaimer: 'No specific service selection or reported role requirements are applied.'
  }
};

export const SERVICE_PROFILE_LIST = SERVICE_PROFILE_IDS.map((id) => SERVICE_PROFILES[id]);

export const getServiceProfile = (serviceProfileId) => SERVICE_PROFILES[serviceProfileId] || null;

export const isModuleInServiceProfile = (serviceProfileId, moduleId) => {
  const profile = getServiceProfile(serviceProfileId);
  if (!profile) return true;
  return profile.moduleIds.includes(moduleId);
};

export const getServiceProfileModules = (serviceProfileId) => {
  const profile = getServiceProfile(serviceProfileId);
  if (!profile) return MODULES;
  return MODULES.filter((module) => profile.moduleIds.includes(module.id));
};
